import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';
import CampaignReport from '../components/CampaignReport';

// ── Placement builder helpers — defined at module level so React never remounts them ──
const mpFmtInput = (v) => v != null && v !== '' && !isNaN(v) ? Number(v).toLocaleString('en-US') : '';
const mpStripFmt = (v) => String(v || '').replace(/,/g, '').trim();

const CustomAddInput = ({ onAdd }) => {
  const [val, setVal] = React.useState('');
  const [open, setOpen] = React.useState(false);
  if (!open) return (
    <button onClick={() => setOpen(true)}
      className="text-xs text-zinc-400 hover:text-white bg-zinc-800 hover:bg-zinc-700 px-2 py-1 rounded border border-dashed border-zinc-700">
      + Custom...
    </button>
  );
  return (
    <div className="flex items-center gap-1">
      <input autoFocus type="text" value={val} placeholder="Line name..."
        onChange={e => setVal(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter' && val.trim()) { onAdd(val.trim()); setVal(''); setOpen(false); } if (e.key === 'Escape') { setVal(''); setOpen(false); } }}
        className="bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-white text-xs w-32 focus:outline-none focus:border-nextide-turquoise"/>
      <button onClick={() => { if (val.trim()) { onAdd(val.trim()); setVal(''); setOpen(false); } }}
        className="text-nextide-turquoise text-xs px-1.5 py-1 bg-zinc-800 rounded hover:bg-zinc-700">+</button>
      <button onClick={() => { setVal(''); setOpen(false); }} className="text-zinc-500 text-xs">✕</button>
    </div>
  );
};

const PlacementRow = ({ p, updatePlacement, deletePlacement, duplicatePlacement, autoCalc, tiers, activeTierId, formatTierLabel }) => {
  const isDescOnly = p.rate_type === 'desc_only';
  const isAV = p.rate_type === 'av';
  const isFlat = p.rate_type === 'flat';
  const isCPM = p.rate_type === 'cpm';
  const [showDates, setShowDates] = React.useState(!!(p.start_date_override || p.end_date_override));
  const [showDupMenu, setShowDupMenu] = React.useState(false);
  const [impVal, setImpVal] = React.useState(mpFmtInput(p.impressions));
  const [cpmVal, setCpmVal] = React.useState(p.cpm != null ? String(p.cpm) : '');
  const [totalVal, setTotalVal] = React.useState(mpFmtInput(p.total));
  const otherTiers = (tiers || []).filter(t => t.id !== activeTierId);

  const saveCalc = (field, rawVal) => {
    const stripped = mpStripFmt(rawVal);
    const u = autoCalc(field, stripped, {
      ...p,
      impressions: field === 'impressions' ? stripped : mpStripFmt(impVal),
      cpm: field === 'cpm' ? stripped : cpmVal,
      total: field === 'total' ? stripped : mpStripFmt(totalVal),
    });
    if (u.impressions != null) setImpVal(mpFmtInput(u.impressions));
    if (u.cpm != null) setCpmVal(String(u.cpm));
    if (u.total != null) setTotalVal(mpFmtInput(u.total));
    updatePlacement(p.id, u);
  };

  return (
    <tr className="border-b border-zinc-800/50 group">
      <td className="px-3 py-2 w-56">
        <input type="text" defaultValue={p.name} onBlur={e => updatePlacement(p.id, { name: e.target.value })}
          className="w-full bg-transparent text-white text-sm focus:outline-none focus:bg-zinc-800 rounded px-1 py-0.5"/>
      </td>
      <td className="px-2 py-2 w-48">
        <input type="text" defaultValue={p.description || ''} placeholder="Description..."
          onBlur={e => updatePlacement(p.id, { description: e.target.value })}
          className="w-full bg-transparent text-zinc-400 text-xs focus:outline-none focus:bg-zinc-800 rounded px-1 py-0.5"/>
      </td>
      <td className="px-2 py-2 w-32 text-right">
        {!isDescOnly && !isAV ? (
          <input type="text" inputMode="numeric" value={impVal} placeholder="—"
            onChange={e => setImpVal(e.target.value)}
            onFocus={e => { setImpVal(mpStripFmt(e.target.value)); e.target.select(); }}
            onBlur={e => { const r = mpStripFmt(e.target.value); setImpVal(mpFmtInput(r)); saveCalc('impressions', r); }}
            className="w-full bg-transparent text-zinc-300 text-xs text-right focus:outline-none focus:bg-zinc-800 rounded px-1 py-0.5"/>
        ) : <span className="text-zinc-600 text-xs">—</span>}
      </td>
      <td className="px-2 py-2 w-20 text-right">
        {isCPM ? (
          <input type="text" inputMode="decimal" value={cpmVal} placeholder="—"
            onChange={e => setCpmVal(e.target.value)}
            onFocus={e => e.target.select()}
            onBlur={e => saveCalc('cpm', e.target.value)}
            className="w-full bg-transparent text-zinc-300 text-xs text-right focus:outline-none focus:bg-zinc-800 rounded px-1 py-0.5"/>
        ) : <span className="text-zinc-600 text-xs">{isFlat ? 'Flat' : '—'}</span>}
      </td>
      <td className="px-2 py-2 w-28 text-right">
        {isAV ? (
          <span className="text-xs px-2 py-0.5 bg-zinc-800 text-zinc-400 rounded">AV</span>
        ) : isDescOnly ? (
          <span className="text-zinc-600 text-xs">—</span>
        ) : (
          <input type="text" inputMode="numeric" value={totalVal} placeholder="—"
            onChange={e => setTotalVal(e.target.value)}
            onFocus={e => { setTotalVal(mpStripFmt(e.target.value)); e.target.select(); }}
            onBlur={e => { const r = mpStripFmt(e.target.value); setTotalVal(mpFmtInput(r)); saveCalc('total', r); }}
            className="w-full bg-transparent text-nextide-turquoise text-xs text-right font-medium focus:outline-none focus:bg-zinc-800 rounded px-1 py-0.5"/>
        )}
      </td>
      <td className="px-2 py-2 w-48">
        {!isDescOnly && (
          <div>
            <button onClick={() => setShowDates(!showDates)}
              className={`text-xs ${showDates || p.start_date_override ? 'text-nextide-turquoise' : 'text-zinc-600 opacity-0 group-hover:opacity-100'}`}>
              {p.start_date_override ? `${new Date(p.start_date_override).toLocaleDateString()} →` : '+ dates'}
            </button>
            {showDates && (
              <div className="flex gap-1 mt-1">
                <input type="date" defaultValue={p.start_date_override?.split('T')[0] || ''}
                  onBlur={e => updatePlacement(p.id, { start_date_override: e.target.value || null })}
                  className="bg-zinc-800 border border-zinc-700 rounded px-1 py-0.5 text-white text-xs w-24"/>
                <input type="date" defaultValue={p.end_date_override?.split('T')[0] || ''}
                  onBlur={e => updatePlacement(p.id, { end_date_override: e.target.value || null })}
                  className="bg-zinc-800 border border-zinc-700 rounded px-1 py-0.5 text-white text-xs w-24"/>
              </div>
            )}
          </div>
        )}
      </td>
      <td className="px-2 py-2 w-16 text-right">
        <div className="flex items-center justify-end gap-1.5">
          {(p.package === 'custom' || p.package === 'av') && otherTiers.length > 0 && (
            <div className="relative">
              <button onClick={() => setShowDupMenu(!showDupMenu)} title="Copy to tier"
                className="text-zinc-600 hover:text-zinc-300 opacity-0 group-hover:opacity-100 text-xs leading-none">⎘</button>
              {showDupMenu && (
                <div className="absolute right-0 bottom-6 bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl z-20 min-w-[130px]">
                  <div className="px-3 py-1.5 text-xs text-zinc-500 border-b border-zinc-700">Copy to:</div>
                  {otherTiers.map(t => (
                    <button key={t.id} onClick={() => { duplicatePlacement(p, t.id); setShowDupMenu(false); }}
                      className="w-full text-left px-3 py-2 text-xs text-zinc-300 hover:bg-zinc-700 hover:text-white">
                      {formatTierLabel(t)}
                    </button>
                  ))}
                  <div className="border-t border-zinc-700">
                    <button onClick={() => { duplicatePlacement(p, activeTierId); setShowDupMenu(false); }}
                      className="w-full text-left px-3 py-2 text-xs text-zinc-400 hover:bg-zinc-700">
                      Duplicate here
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          <button onClick={() => deletePlacement(p.id)}
            className="text-zinc-600 hover:text-red-400 text-xs leading-none">×</button>
        </div>
      </td>
    </tr>
  );
};

const DealDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [deal, setDeal] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [activities, setActivities] = useState([]);
  const [files, setFiles] = useState([]);
  const [stages, setStages] = useState({});
  const [products, setProducts] = useState([]);
  const [fixedCosts, setFixedCosts] = useState(['Creative Production', 'Misc Fees', 'Travel Fees']);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [showAddContact, setShowAddContact] = useState(false);
  const [availableContacts, setAvailableContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState('');
  const [showAddActivity, setShowAddActivity] = useState(false);
  const [newActivity, setNewActivity] = useState({ activity_type: 'note', subject: '', description: '' });
  const [uploading, setUploading] = useState(false);
  const [sellers, setSellers] = useState([]);
  const [partnerSellers, setPartnerSellers] = useState([]);
  const [talentList, setTalentList] = useState(null);
  const [talentItems, setTalentItems] = useState([]);
  const [allTalentLists, setAllTalentLists] = useState([]);
  const [linkingList, setLinkingList] = useState(false);

  // Tiers
  const [tiers, setTiers] = useState([]);
  const [selectedTierIdx, setSelectedTierIdx] = useState(0);
  const [tierForms, setTierForms] = useState({}); // { [tierId]: { total_deal_value, cost_breakdown } }
  const [tierSaving, setTierSaving] = useState(false);

  // Placements
  const [placements, setPlacements] = useState([]);
  const [activeTierId, setActiveTierId] = useState(null);
  const [placementsLoading, setPlacementsLoading] = useState(false);
  const [customPackageName, setCustomPackageName] = useState('Custom Activation');
  const [editingCustomName, setEditingCustomName] = useState(false);

  // IO state
  const [ioData, setIoData] = useState(null);
  const [billingEntities, setBillingEntities] = useState([]);
  const [ioSaving, setIoSaving] = useState(false);
  const [showBillToModal, setShowBillToModal] = useState(false);
  const [billToForm, setBillToForm] = useState({ company_name: '', attention: '', address_line1: '', address_line2: '', city: '', state: '', zip: '', country: 'USA', email: '', is_default: false });
  const [editingBillingId, setEditingBillingId] = useState(null);
  const [ioForm, setIoForm] = useState({ io_number: '', billing_entity_id: '', payment_terms: null, notes: '' });
  const [ioPaymentTerms, setIoPaymentTerms] = useState([
    { label: 'Deposit (25%)', pct: 25, net: 30 },
    { label: 'Remaining Balance (75%)', pct: 75, net: 60 },
  ]);

  const MP_STAGES = ['rfp_received', 'rfp_submitted', 'client_recommended', 'verbal', 'io_sent', 'closed_won'];
  const IO_STAGES = ['verbal', 'io_sent', 'closed_won'];

  const verticals = [
    { id: 'gaming', label: 'Gaming' }, { id: 'entertainment', label: 'Entertainment' },
    { id: 'cpg', label: 'CPG' }, { id: 'foodbev', label: 'Food & Bev' }, { id: 'qsr', label: 'QSR' },
    { id: 'insurance', label: 'Insurance' }, { id: 'finance', label: 'Finance' }, { id: 'auto', label: 'Auto' },
    { id: 'retail', label: 'Retail' }, { id: 'sports', label: 'Sports' }, { id: 'tech', label: 'Tech' },
    { id: 'telco', label: 'Telco' }, { id: 'other', label: 'Other' }
  ];

  useEffect(() => { fetchDeal(); fetchSellers(); fetchAllContacts(); fetchTalentList(); fetchAllTalentLists(); fetchTiers(); }, [id]);

  useEffect(() => {
    if (activeTab === 'media plan') {
      const tierId = deal?.tiers_enabled && tiers.length > 0 ? tiers[selectedTierIdx]?.id : null;
      fetchPlacements(tierId);
    }
    if (activeTab === 'io') {
      fetchIOData();
    }
  }, [activeTab, selectedTierIdx, tiers.length]);

  const fetchDeal = async () => {
    try {
      const res = await api.get(`/deals/${id}`);
      setDeal(res.data.deal); setContacts(res.data.contacts); setActivities(res.data.activities);
      setFiles(res.data.files); setStages(res.data.stages); setProducts(res.data.products);
      if (res.data.fixedCosts) setFixedCosts(res.data.fixedCosts);
      setEditForm({ ...res.data.deal, cost_breakdown: res.data.deal.cost_breakdown || {} });
    } catch (e) { navigate('/crm?tab=pipeline'); } finally { setLoading(false); }
  };

  const fetchSellers = async () => { try { const res = await api.get('/sellers'); setSellers(res.data.sellers); } catch (e) {} };
  useEffect(() => {
    const pid = editing ? editForm?.partner_id : deal?.partner_id;
    const src = editing ? editForm?.deal_source : deal?.deal_source;
    if (src === 'partner' && pid) {
      api.get(`/sales-partners/${pid}/sellers`).then(r => setPartnerSellers(r.data.sellers || [])).catch(() => setPartnerSellers([]));
    } else {
      setPartnerSellers([]);
    }
  }, [deal?.partner_id, deal?.deal_source, editForm?.partner_id, editForm?.deal_source, editing]);
  const fetchAllContacts = async () => { try { const res = await api.get('/contacts'); setAvailableContacts(res.data.contacts); } catch (e) {} };
  const fetchTalentList = async () => { try { const res = await api.get(`/deals/${id}/talent-list`); setTalentList(res.data.list); setTalentItems(res.data.items || []); } catch (e) {} };
  const fetchAllTalentLists = async () => { try { const res = await api.get('/talent-lists'); setAllTalentLists(res.data.lists || []); } catch (e) {} };

  const fetchTiers = async () => {
    try {
      const res = await api.get(`/deals/${id}/tiers`);
      const t = res.data.tiers || [];
      setTiers(t);
      // Initialise tierForms from fetched data
      const forms = {};
      t.forEach(tier => {
        forms[tier.id] = {
          total_deal_value: tier.total_deal_value || '',
          cost_breakdown: tier.cost_breakdown || {},
          custom_package_name: tier.custom_package_name || 'Custom Activation',
        };
      });
      setTierForms(forms);
    } catch (e) {}
  };

  const addTier = async () => {
    try {
      const res = await api.post(`/deals/${id}/tiers`, { custom_package_name: 'Custom Activation' });
      const newTier = res.data.tier;
      setTiers(prev => [...prev, newTier]);
      setTierForms(prev => ({ ...prev, [newTier.id]: { total_deal_value: '', cost_breakdown: {}, custom_package_name: 'Custom Activation' } }));
      setSelectedTierIdx(tiers.length); // select new tier
    } catch (e) { alert('Failed to add tier'); }
  };

  const deleteTier = async (tierId, tierIdx) => {
    if (!window.confirm('Delete this tier?')) return;
    try {
      await api.delete(`/deals/${id}/tiers/${tierId}`);
      setTiers(prev => prev.filter(t => t.id !== tierId));
      setTierForms(prev => { const n = { ...prev }; delete n[tierId]; return n; });
      setSelectedTierIdx(prev => Math.max(0, prev - (tierIdx <= prev ? 1 : 0)));
    } catch (e) { alert('Failed to delete tier'); }
  };

  const saveTier = async (tierId) => {
    const form = tierForms[tierId];
    if (!form) return;
    setTierSaving(true);
    try {
      const res = await api.put(`/deals/${id}/tiers/${tierId}`, {
        total_deal_value: parseFloat(form.total_deal_value) || 0,
        cost_breakdown: form.cost_breakdown,
      });
      setTiers(prev => prev.map(t => t.id === tierId ? res.data.tier : t));
    } catch (e) { alert('Failed to save tier'); }
    finally { setTierSaving(false); }
  };

  const updateTierForm = (tierId, field, value) => {
    setTierForms(prev => ({ ...prev, [tierId]: { ...prev[tierId], [field]: value } }));
  };

  const updateTierCost = (tierId, key, value) => {
    setTierForms(prev => ({
      ...prev,
      [tierId]: { ...prev[tierId], cost_breakdown: { ...prev[tierId].cost_breakdown, [key]: value } }
    }));
  };

  const formatTierLabel = (tier) => {
    const val = parseFloat(tier.total_deal_value) || 0;
    const fmt = val >= 1e6 ? `$${(val / 1e6).toFixed(val % 1e6 === 0 ? 0 : 2)}M`
      : val >= 1e3 ? `$${Math.round(val / 1e3)}K`
      : val > 0 ? formatCurrency(val) : '';
    return `Tier ${tier.tier_number}${fmt ? ` | ${fmt}` : ''}`;
  };

  // ---- Placement helpers ----
  const fetchPlacements = async (tierId) => {
    setPlacementsLoading(true);
    try {
      const url = tierId ? `/deals/${id}/placements?tier_id=${tierId}` : `/deals/${id}/placements`;
      const res = await api.get(url);
      setPlacements(res.data.placements || []);
      setActiveTierId(res.data.tier_id || tierId);
    } catch (e) { console.error('fetchPlacements error', e); }
    finally { setPlacementsLoading(false); }
  };

  const fetchIOData = async () => {
    try {
      const [ioRes, tiersRes] = await Promise.all([
        api.get(`/deals/${id}/io`),
        api.get(`/deals/${id}/tiers`),
      ]);

      // Fetch placements for each tier
      const tierRows = tiersRes.data.tiers || [];
      const tiersWithPlacements = await Promise.all(
        tierRows.map(async (tier) => {
          try {
            const pRes = await api.get(`/deals/${id}/placements?tier_id=${tier.id}`);
            return { ...tier, placements: pRes.data.placements || [] };
          } catch (e) { return { ...tier, placements: [] }; }
        })
      );
      // Update tiers state so IO tab has placements without needing to visit Media Plan first
      if (tiersWithPlacements.length > 0) setTiers(tiersWithPlacements);

      const io = ioRes.data.io;
      setBillingEntities(ioRes.data.billingEntities || []);
      if (io) {
        setIoData(io);
        setIoForm({
          io_number: io.io_number || '',
          billing_entity_id: io.billing_entity_id || '',
          notes: io.notes || '',
        });
        if (io.payment_terms) setIoPaymentTerms(io.payment_terms);
      }
    } catch (e) { console.error('fetchIOData error', e); }
  };

  const saveIO = async () => {
    setIoSaving(true);
    try {
      await api.put(`/deals/${id}/io`, {
        ...ioForm,
        payment_terms: ioPaymentTerms,
      });
      await fetchIOData();
    } catch (e) { alert('Failed to save IO'); }
    finally { setIoSaving(false); }
  };

  const exportIO = async () => {
    try {
      const res = await api.get(`/deals/${id}/io/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(deal.name || 'IO').replace(/[^a-zA-Z0-9 ]/g, '')}_IO.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) { alert('Export failed'); }
  };

  const saveBillingEntity = async () => {
    try {
      if (editingBillingId) {
        await api.put(`/billing-entities/${editingBillingId}`, billToForm);
      } else {
        await api.post('/billing-entities', billToForm);
      }
      await fetchIOData();
      setShowBillToModal(false);
      setBillToForm({ company_name: '', attention: '', address_line1: '', address_line2: '', city: '', state: '', zip: '', country: 'USA', email: '', is_default: false });
      setEditingBillingId(null);
    } catch (e) { alert('Failed to save billing entity'); }
  };

  const deleteBillingEntity = async (beId) => {
    if (!window.confirm('Delete this billing entity?')) return;
    try {
      await api.delete(`/billing-entities/${beId}`);
      await fetchIOData();
      if (ioForm.billing_entity_id === beId) setIoForm(f => ({ ...f, billing_entity_id: '' }));
    } catch (e) { alert('Failed to delete'); }
  };

  const addPlacement = async (pkg, name, rateType = 'cpm') => {
    if (!activeTierId) return;
    try {
      const res = await api.post(`/deals/${id}/placements`, { tier_id: activeTierId, package: pkg, name, rate_type: rateType });
      setPlacements(prev => [...prev, res.data.placement]);
    } catch (e) { alert('Failed to add placement'); }
  };

  const updatePlacement = async (placementId, updates) => {
    // Optimistic update
    setPlacements(prev => prev.map(p => p.id === placementId ? { ...p, ...updates } : p));
    try {
      await api.put(`/deals/${id}/placements/${placementId}`, updates);
    } catch (e) {
      // Revert on fail
      fetchPlacements(activeTierId);
    }
  };

  const deletePlacement = async (placementId) => {
    setPlacements(prev => prev.filter(p => p.id !== placementId));
    try { await api.delete(`/deals/${id}/placements/${placementId}`); }
    catch (e) { fetchPlacements(activeTierId); }
  };

  const duplicatePlacement = async (placement, targetTierId) => {
    try {
      const res = await api.post(`/deals/${id}/placements`, {
        tier_id: targetTierId,
        package: placement.package,
        name: placement.name,
        description: placement.description,
        rate_type: placement.rate_type,
        impressions: placement.impressions,
        cpm: placement.cpm,
        total: placement.total,
      });
      // If copying to active tier, append to local state
      if (targetTierId === activeTierId) {
        setPlacements(prev => [...prev, res.data.placement]);
      }
    } catch (e) { alert('Failed to copy placement'); }
  };

  // Auto-calc: given two of (impressions, cpm, total), derive the third
  const autoCalc = (field, value, placement) => {
    const updates = { [field]: value === '' ? null : (field === 'impressions' ? parseInt(value) : parseFloat(value)) };
    const imp = field === 'impressions' ? parseInt(value) : parseInt(placement.impressions);
    const cpm = field === 'cpm' ? parseFloat(value) : parseFloat(placement.cpm);
    const total = field === 'total' ? parseFloat(value) : parseFloat(placement.total);
    if (field === 'impressions' && imp > 0 && cpm > 0) updates.total = parseFloat(((imp / 1000) * cpm).toFixed(2));
    if (field === 'impressions' && imp > 0 && total > 0) updates.cpm = parseFloat(((total / imp) * 1000).toFixed(2));
    if (field === 'cpm' && cpm > 0 && imp > 0) updates.total = parseFloat(((imp / 1000) * cpm).toFixed(2));
    if (field === 'cpm' && cpm > 0 && total > 0) updates.impressions = Math.round((total / cpm) * 1000);
    if (field === 'total' && total > 0 && cpm > 0) updates.impressions = Math.round((total / cpm) * 1000);
    if (field === 'total' && total > 0 && imp > 0) updates.cpm = parseFloat(((total / imp) * 1000).toFixed(2));
    return updates;
  };

  // Tier totals for the active tier
  const tierTotals = useMemo(() => {
    const totalImpressions = placements.reduce((s, p) => s + (parseInt(p.impressions) || 0), 0);
    const totalRevenue = placements.filter(p => p.rate_type !== 'av').reduce((s, p) => s + (parseFloat(p.total) || 0), 0);
    const blendedCpm = totalImpressions > 0 && totalRevenue > 0 ? (totalRevenue / totalImpressions * 1000).toFixed(2) : null;
    return { totalImpressions, totalRevenue, blendedCpm };
  }, [placements]);
  const linkTalentList = async (listId) => {
    setLinkingList(true);
    try { await api.put(`/deals/${id}/talent-list`, { talent_list_id: listId || null }); fetchTalentList(); fetchDeal(); }
    catch (e) { alert('Failed to link'); } finally { setLinkingList(false); }
  };

  const saveDeal = async () => {
    setSaving(true);
    try { await api.put(`/deals/${id}`, { ...editForm, tiers_enabled: editForm.tiers_enabled }); setEditing(false); fetchDeal(); fetchTiers(); }
    catch (e) { alert('Failed to save'); } finally { setSaving(false); }
  };

  const updateStage = async (newStage) => {
    try { await api.patch(`/deals/${id}/stage`, { stage: newStage }); fetchDeal(); }
    catch (e) { alert('Failed'); }
  };

  const deleteDeal = async () => {
    if (!window.confirm('Delete this deal?')) return;
    try { await api.delete(`/deals/${id}`); navigate('/crm?tab=pipeline'); } catch (e) { alert('Failed'); }
  };

  const addContact = async () => {
    if (!selectedContact) return;
    try { await api.post(`/deals/${id}/contacts`, { contact_id: selectedContact, is_primary: contacts.length === 0 }); setShowAddContact(false); setSelectedContact(''); fetchDeal(); }
    catch (e) { alert('Failed'); }
  };

  const removeContact = async (contactId) => {
    if (!window.confirm('Remove contact?')) return;
    try { await api.delete(`/deals/${id}/contacts/${contactId}`); fetchDeal(); } catch (e) {}
  };

  const addActivity = async (e) => {
    e.preventDefault();
    try { await api.post(`/deals/${id}/activities`, newActivity); setShowAddActivity(false); setNewActivity({ activity_type: 'note', subject: '', description: '' }); fetchDeal(); }
    catch (e) { alert('Failed'); }
  };

  const uploadFile = async (e, fileType) => {
    const file = e.target.files[0]; if (!file) return;
    setUploading(true);
    const formData = new FormData(); formData.append('file', file); formData.append('file_type', fileType);
    try { await api.post(`/deals/${id}/files`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }); fetchDeal(); }
    catch (e) { alert('Failed'); } finally { setUploading(false); }
  };

  const deleteFile = async (fileId) => {
    if (!window.confirm('Delete file?')) return;
    try { await api.delete(`/deals/${id}/files/${fileId}`); fetchDeal(); } catch (e) {}
  };

  const downloadFile = async (fileId, fileName) => {
    try {
      const res = await api.get(`/deals/${id}/files/${fileId}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a'); link.href = url; link.setAttribute('download', fileName);
      document.body.appendChild(link); link.click(); link.remove();
    } catch (e) { alert('Failed'); }
  };

  const toggleProduct = (product) => {
    const current = editForm.products_utilized || [];
    const newProducts = current.includes(product) ? current.filter(p => p !== product) : [...current, product];
    const newCosts = { ...editForm.cost_breakdown }; if (current.includes(product)) delete newCosts[product];
    setEditForm({ ...editForm, products_utilized: newProducts, cost_breakdown: newCosts });
  };

  const updateCost = (key, value) => setEditForm(prev => ({ ...prev, cost_breakdown: { ...prev.cost_breakdown, [key]: value } }));

  const calculateMargins = (data) => {
    const dealValue = parseFloat(data.total_deal_value) || 0;
    const isPartner = data.deal_source === 'partner' && data.partner_rev_share;
    const revSharePct = parseFloat(data.partner_rev_share) || 0;
    const partnerNetValue = isPartner ? dealValue * (revSharePct / 100) : dealValue;
    let totalHardCosts = 0;
    if (data.cost_breakdown) Object.values(data.cost_breakdown).forEach(c => { totalHardCosts += parseFloat(c) || 0; });
    const commission = isPartner ? 0 : dealValue * 0.10;
    const referrerPct = parseFloat(data.partner_referrer_pct) || 0;
    const referrerCommission = isPartner && referrerPct > 0 ? partnerNetValue * (referrerPct / 100) : 0;
    const netRevenue = partnerNetValue - totalHardCosts - commission - referrerCommission;
    const grossMarginPercent = partnerNetValue > 0 ? ((netRevenue / partnerNetValue) * 100).toFixed(1) : 0;
    return { dealValue, totalHardCosts, commission, referrerCommission, referrerPct, netRevenue, grossMarginPercent, isPartner, revSharePct, partnerNetValue };
  };

  const formatCurrency = (amt) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(amt || 0);
  const formatDate = (d) => d ? new Date(d).toLocaleDateString() : '-';
  const formatDateTime = (d) => d ? new Date(d).toLocaleString() : '-';

  if (loading) return <div className="p-8 text-zinc-400">Loading...</div>;

  const linkedContactIds = contacts.map(c => c.id);
  const unlinkedContacts = availableContacts.filter(c => !linkedContactIds.includes(c.id));
  const margins = calculateMargins(editing ? editForm : deal);
  const costBreakdown = (editing ? editForm.cost_breakdown : deal.cost_breakdown) || {};

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <button onClick={() => navigate('/crm?tab=pipeline')} className="text-zinc-400 hover:text-white mb-2 text-sm">← Back to Pipeline</button>
          <h1 className="text-2xl font-bold text-white">{deal.name}</h1>
          <div className="flex items-center gap-4 mt-2">
            <span className={`px-3 py-1 rounded-lg text-sm font-medium ${deal.stage === 'closed_won' ? 'bg-green-500/20 text-green-400' : deal.stage === 'closed_lost' ? 'bg-red-500/20 text-red-400' : 'bg-zinc-800 text-zinc-300'}`}>{stages[deal.stage]?.label} ({stages[deal.stage]?.percent}%)</span>
            <span className="text-nextide-turquoise font-bold text-lg">{formatCurrency(deal.total_deal_value)}</span>
          </div>
        </div>
        <div className="flex gap-3">
          {!editing ? (
            <><button onClick={() => setEditing(true)} className="bg-zinc-800 text-white px-4 py-2 rounded-lg">Edit</button><button onClick={deleteDeal} className="bg-red-500/20 text-red-400 px-4 py-2 rounded-lg">Delete</button></>
          ) : (
            <><button onClick={() => { setEditing(false); setEditForm({ ...deal, cost_breakdown: deal.cost_breakdown || {} }); }} className="bg-zinc-800 text-white px-4 py-2 rounded-lg">Cancel</button><button onClick={saveDeal} disabled={saving} className="bg-nextide-turquoise text-black px-4 py-2 rounded-lg disabled:opacity-50">{saving ? 'Saving...' : 'Save'}</button></>
          )}
        </div>
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 mb-6">
        <div className="flex items-center gap-1 overflow-x-auto">
          {Object.entries(stages).map(([key, stage]) => (
            <button key={key} onClick={() => !editing && updateStage(key)} disabled={editing} className={`flex-shrink-0 px-3 py-2 rounded-lg text-xs font-medium ${key === deal.stage ? (key === 'closed_won' ? 'bg-green-500 text-white' : key === 'closed_lost' ? 'bg-red-500 text-white' : 'bg-nextide-turquoise text-black') : 'bg-zinc-800 text-zinc-500 hover:bg-zinc-700'}`}>{stage.label}</button>
          ))}
        </div>
      </div>

      <div className="flex gap-4 mb-6 border-b border-zinc-800">
        {['overview', ...(MP_STAGES.includes(deal.stage) ? ['media plan'] : []), ...(IO_STAGES.includes(deal.stage) ? ['io'] : []), 'talent', 'reports', 'contacts', 'files', 'activity'].map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)} className={`pb-3 px-2 font-medium capitalize ${activeTab === tab ? 'text-nextide-turquoise border-b-2 border-nextide-turquoise' : 'text-zinc-400'}`}>
            {tab} {tab === 'contacts' && `(${contacts.length})`}{tab === 'activity' && `(${activities.length})`}{tab === 'files' && `(${files.length})`}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && (
        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 space-y-6">
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Deal Information</h3>
              {editing ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div><label className="block text-sm text-zinc-400 mb-1">Name</label><input type="text" value={editForm.name || ''} onChange={e => setEditForm({...editForm, name: e.target.value})} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"/></div>
                    <div><label className="block text-sm text-zinc-400 mb-1">Account</label><input type="text" value={editForm.account || ''} onChange={e => setEditForm({...editForm, account: e.target.value})} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"/></div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><label className="block text-sm text-zinc-400 mb-1">Customer Type</label><select value={editForm.customer_type || ''} onChange={e => setEditForm({...editForm, customer_type: e.target.value, brand_name: '', reseller_name: ''})} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"><option value="brand">Brand Direct</option><option value="agency">Agency</option><option value="reseller">Reseller</option></select></div>
                    <div><label className="block text-sm text-zinc-400 mb-1">Vertical</label><select value={editForm.vertical || ''} onChange={e => setEditForm({...editForm, vertical: e.target.value})} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white">{verticals.map(v => <option key={v.id} value={v.id}>{v.label}</option>)}</select></div>
                  </div>
                  {editForm.customer_type === 'agency' && (
                    <div><label className="block text-sm text-zinc-400 mb-1">Which Agency?</label><input type="text" value={editForm.brand_name || ''} onChange={e => setEditForm({...editForm, brand_name: e.target.value})} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white" placeholder="Enter agency name"/></div>
                  )}
                  {editForm.customer_type === 'reseller' && (
                    <div><label className="block text-sm text-zinc-400 mb-1">Which Reseller?</label><input type="text" value={editForm.reseller_name || ''} onChange={e => setEditForm({...editForm, reseller_name: e.target.value})} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white" placeholder="Enter reseller name"/></div>
                  )}
                  <div className="grid grid-cols-3 gap-4">
                    <div><label className="block text-sm text-zinc-400 mb-1">Campaign Start</label><input type="date" value={editForm.campaign_start_date?.split('T')[0] || ''} onChange={e => setEditForm({...editForm, campaign_start_date: e.target.value})} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"/></div>
                    <div><label className="block text-sm text-zinc-400 mb-1">Campaign End</label><input type="date" value={editForm.campaign_end_date?.split('T')[0] || ''} onChange={e => setEditForm({...editForm, campaign_end_date: e.target.value})} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"/></div>
                    <div><label className="block text-sm text-zinc-400 mb-1">Expected Close</label><input type="date" value={editForm.expected_close_date?.split('T')[0] || ''} onChange={e => setEditForm({...editForm, expected_close_date: e.target.value})} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"/></div>
                  </div>
                  <div><label className="block text-sm text-zinc-400 mb-1">Seller</label><select value={editForm.deal_source === 'partner' ? (editForm.partner_seller_id || '') : (editForm.seller_id || '')} onChange={e => { if (editForm.deal_source === 'partner') setEditForm({...editForm, partner_seller_id: e.target.value}); else setEditForm({...editForm, seller_id: e.target.value}); }} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"><option value="">Select...</option>{(editForm.deal_source === 'partner' && editForm.partner_id ? partnerSellers : sellers).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</select></div>
                  <div><label className="block text-sm text-zinc-400 mb-2">Products</label><div className="flex flex-wrap gap-2">{products.map(p => <button key={p} type="button" onClick={() => toggleProduct(p)} className={`px-3 py-1 rounded-lg text-sm ${(editForm.products_utilized || []).includes(p) ? 'bg-nextide-turquoise text-black' : 'bg-zinc-800 text-zinc-400'}`}>{p}</button>)}</div></div>
                  <div><label className="block text-sm text-zinc-400 mb-1">Notes</label><textarea value={editForm.notes || ''} onChange={e => setEditForm({...editForm, notes: e.target.value})} rows={3} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"/></div>
                  <div className="flex items-center gap-3 pt-2 border-t border-zinc-800">
                    <span className="text-sm text-zinc-400">Multiple Tiers</span>
                    <button type="button" onClick={() => setEditForm({...editForm, tiers_enabled: !editForm.tiers_enabled})}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${editForm.tiers_enabled ? 'bg-nextide-turquoise' : 'bg-zinc-700'}`}>
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${editForm.tiers_enabled ? 'translate-x-6' : 'translate-x-1'}`}/>
                    </button>
                    <span className="text-xs text-zinc-500">{editForm.tiers_enabled ? 'Each tier has its own media plan and margin calc' : 'Single plan deal'}</span>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><span className="text-zinc-500">Account:</span> <span className="text-white ml-2">{deal.account || '-'}</span></div>
                  <div><span className="text-zinc-500">Customer:</span> <span className="text-white ml-2 capitalize">{deal.customer_type?.replace('_', ' ')}</span></div>
                  {deal.customer_type === 'agency' && deal.brand_name && <div><span className="text-zinc-500">Agency:</span> <span className="text-white ml-2">{deal.brand_name}</span></div>}
                  {deal.customer_type === 'reseller' && deal.reseller_name && <div><span className="text-zinc-500">Reseller:</span> <span className="text-white ml-2">{deal.reseller_name}</span></div>}
                  {deal.deal_source === 'partner' && <div><span className="text-zinc-500">Deal Source:</span> <span className="text-purple-400 ml-2">Partner ({deal.partner_rev_share}% NexTide)</span></div>}
                  {deal.deal_source === 'partner' && deal.referrer_name && <div><span className="text-zinc-500">Referrer:</span> <span className="text-yellow-400 ml-2">{deal.referrer_name} ({deal.partner_referrer_pct || 0}%)</span>{deal.referrer_commission > 0 && <span className="text-zinc-400 ml-1">= ${parseFloat(deal.referrer_commission).toLocaleString()}</span>}</div>}
                  <div><span className="text-zinc-500">Vertical:</span> <span className="text-white ml-2 capitalize">{deal.vertical}</span></div>
                  <div><span className="text-zinc-500">Seller:</span> <span className="text-white ml-2">{deal.deal_source === 'partner' ? (deal.partner_seller_name || deal.seller_name || '-') : (deal.seller_name || '-')}</span></div>
                  <div><span className="text-zinc-500">Campaign:</span> <span className="text-white ml-2">{formatDate(deal.campaign_start_date)} - {formatDate(deal.campaign_end_date)}</span></div>
                  <div><span className="text-zinc-500">Expected Close:</span> <span className="text-white ml-2">{formatDate(deal.expected_close_date)}</span></div>
                  {deal.products_utilized?.length > 0 && <div className="col-span-2"><span className="text-zinc-500">Products:</span><div className="flex flex-wrap gap-1 mt-1">{deal.products_utilized.map(p => <span key={p} className="px-2 py-0.5 bg-zinc-800 text-zinc-300 rounded text-xs">{p}</span>)}</div></div>}
                  {deal.notes && <div className="col-span-2"><span className="text-zinc-500">Notes:</span><div className="text-zinc-300 mt-1">{deal.notes}</div></div>}
                </div>
              )}
            </div>

            {/* Margin Calculator (Edit Mode) */}
            {editing && (
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Margin Calculator</h3>

                {editForm.tiers_enabled ? (
                  <div>
                    {/* Tier tabs */}
                    <div className="flex items-center gap-2 mb-4 flex-wrap">
                      {tiers.map((tier, idx) => (
                        <button key={tier.id} onClick={() => setSelectedTierIdx(idx)}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium ${selectedTierIdx === idx ? 'bg-nextide-turquoise text-black' : 'bg-zinc-800 text-zinc-300'}`}>
                          {formatTierLabel(tier)}
                        </button>
                      ))}
                      {tiers.length < 3 && (
                        <button onClick={addTier} className="px-3 py-1.5 rounded-lg text-xs bg-zinc-800 text-zinc-400 hover:text-white border border-zinc-700 border-dashed">
                          + Add Tier
                        </button>
                      )}
                    </div>
                    {tiers.length === 0 && (
                      <div className="text-center py-6">
                        <p className="text-zinc-500 text-sm mb-3">No tiers yet — add your first tier to get started</p>
                        <button onClick={addTier} className="bg-nextide-turquoise text-black px-4 py-2 rounded-lg text-sm font-semibold">+ Add Tier 1</button>
                      </div>
                    )}
                    {tiers.length > 0 && (() => {
                      const tier = tiers[selectedTierIdx];
                      if (!tier) return null;
                      const tf = tierForms[tier.id] || {};
                      return (
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm text-zinc-400 mb-1">Total Deal Value</label>
                            <div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400">$</span>
                              <input type="number" value={tf.total_deal_value || ''} onChange={e => updateTierForm(tier.id, 'total_deal_value', e.target.value)}
                                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg pl-8 pr-4 py-2 text-white text-lg font-bold"/>
                            </div>
                          </div>
                          {(editForm.products_utilized || []).length > 0 && (
                            <div><div className="text-sm text-zinc-500 mb-2">Product Costs</div>
                              <div className="grid grid-cols-2 gap-3">
                                {(editForm.products_utilized || []).map(p => (
                                  <div key={p} className="flex items-center gap-2">
                                    <label className="text-zinc-300 text-sm w-32 truncate">{p}</label>
                                    <div className="relative flex-1"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 text-sm">$</span>
                                      <input type="number" value={tf.cost_breakdown?.[p] || ''} onChange={e => updateTierCost(tier.id, p, e.target.value)}
                                        className="w-full bg-zinc-800 border border-zinc-700 rounded-lg pl-7 pr-3 py-2 text-white text-sm"/>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          <div><div className="text-sm text-zinc-500 mb-2">Additional Costs</div>
                            <div className="grid grid-cols-2 gap-3">
                              {fixedCosts.map(c => (
                                <div key={c} className="flex items-center gap-2">
                                  <label className="text-zinc-300 text-sm w-32">{c}</label>
                                  <div className="relative flex-1"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 text-sm">$</span>
                                    <input type="number" value={tf.cost_breakdown?.[c] || ''} onChange={e => updateTierCost(tier.id, c, e.target.value)}
                                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg pl-7 pr-3 py-2 text-white text-sm"/>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className="flex items-center justify-between pt-2">
                            <button onClick={() => deleteTier(tier.id, selectedTierIdx)}
                              className="text-red-400 hover:text-red-300 text-xs">Delete Tier {tier.tier_number}</button>
                            <button onClick={() => saveTier(tier.id)} disabled={tierSaving}
                              className="bg-zinc-700 text-white px-4 py-1.5 rounded-lg text-sm disabled:opacity-50">
                              {tierSaving ? 'Saving...' : 'Save Tier'}
                            </button>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div><label className="block text-sm text-zinc-400 mb-1">Total Deal Value</label><div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400">$</span><input type="number" value={editForm.total_deal_value || ''} onChange={e => setEditForm({...editForm, total_deal_value: e.target.value})} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg pl-8 pr-4 py-2 text-white text-lg font-bold"/></div></div>
                    {(editForm.products_utilized || []).length > 0 && <div><div className="text-sm text-zinc-500 mb-2">Product Costs</div><div className="grid grid-cols-2 gap-3">{(editForm.products_utilized || []).map(p => <div key={p} className="flex items-center gap-2"><label className="text-zinc-300 text-sm w-32 truncate">{p}</label><div className="relative flex-1"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 text-sm">$</span><input type="number" value={editForm.cost_breakdown[p] || ''} onChange={e => updateCost(p, e.target.value)} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg pl-7 pr-3 py-2 text-white text-sm"/></div></div>)}</div></div>}
                    <div><div className="text-sm text-zinc-500 mb-2">Additional Costs</div><div className="grid grid-cols-2 gap-3">{fixedCosts.map(c => <div key={c} className="flex items-center gap-2"><label className="text-zinc-300 text-sm w-32">{c}</label><div className="relative flex-1"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 text-sm">$</span><input type="number" value={editForm.cost_breakdown[c] || ''} onChange={e => updateCost(c, e.target.value)} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg pl-7 pr-3 py-2 text-white text-sm"/></div></div>)}</div></div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="space-y-6">
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white">Financials</h3>
                {deal.tiers_enabled && tiers.length > 0 && (
                  <div className="flex gap-1">
                    {tiers.map((tier, idx) => (
                      <button key={tier.id} onClick={() => setSelectedTierIdx(idx)}
                        className={`px-2 py-1 rounded text-xs font-medium ${selectedTierIdx === idx ? 'bg-nextide-turquoise text-black' : 'bg-zinc-800 text-zinc-400 hover:text-white'}`}>
                        T{tier.tier_number}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {(() => {
                // When tiers enabled and tiers exist, use selected tier's data for margin calc
                const tiersActive = deal.tiers_enabled && tiers.length > 0;
                const selectedTier = tiersActive ? tiers[selectedTierIdx] : null;
                const calcData = selectedTier
                  ? { ...deal, total_deal_value: selectedTier.total_deal_value, cost_breakdown: selectedTier.cost_breakdown || {} }
                  : (editing ? editForm : deal);
                const m = calculateMargins(calcData);
                return (
                  <div className="space-y-3">
                    {tiersActive && selectedTier && (
                      <div className="text-xs text-zinc-500 mb-1">{formatTierLabel(selectedTier)}</div>
                    )}
                    <div className="flex justify-between text-sm"><span className="text-zinc-400">Deal Value</span><span className="text-white font-medium">{formatCurrency(m.dealValue)}</span></div>
                    {m.isPartner && (
                      <>
                        <div className="flex justify-between text-sm"><span className="text-purple-400">Rev Share ({m.revSharePct}% NexTide)</span><span className="text-purple-300">- {formatCurrency(m.dealValue - m.partnerNetValue)}</span></div>
                        <div className="flex justify-between text-sm border-t border-zinc-700 pt-2"><span className="text-purple-300 font-semibold">Net to NexTide</span><span className="text-purple-300 font-bold">{formatCurrency(m.partnerNetValue)}</span></div>
                      </>
                    )}
                    <div className="flex justify-between text-sm"><span className="text-zinc-400">Hard Costs</span><span className="text-red-400">- {formatCurrency(m.totalHardCosts)}</span></div>
                    {!m.isPartner && <div className="flex justify-between text-sm"><span className="text-zinc-400">Commission (10%)</span><span className="text-red-400">- {formatCurrency(m.commission)}</span></div>}
                    {m.isPartner && m.referrerCommission > 0 && <div className="flex justify-between text-sm"><span className="text-yellow-400">Referrer ({m.referrerPct}%)</span><span className="text-yellow-400">- {formatCurrency(m.referrerCommission)}</span></div>}
                    <hr className="border-zinc-700"/>
                    <div className="flex justify-between"><span className="text-white font-semibold">Net Revenue</span><span className={`font-bold ${m.netRevenue >= 0 ? 'text-green-400' : 'text-red-400'}`}>{formatCurrency(m.netRevenue)}</span></div>
                    <div className="flex justify-between"><span className="text-white font-semibold">Margin</span><span className={`font-bold ${parseFloat(m.grossMarginPercent) >= 0 ? 'text-green-400' : 'text-red-400'}`}>{m.grossMarginPercent}%</span></div>
                    <hr className="border-zinc-700"/>
                    <div className="flex justify-between text-sm"><span className="text-zinc-400">Weighted Value</span><span className="text-nextide-turquoise font-medium">{formatCurrency((m.isPartner ? m.partnerNetValue : m.dealValue) * (stages[deal.stage]?.percent || 0) / 100)}</span></div>
                  </div>
                );
              })()}
            </div>

            {(() => {
              const tiersActive = deal.tiers_enabled && tiers.length > 0;
              const selectedTier = tiersActive ? tiers[selectedTierIdx] : null;
              const cb = selectedTier ? (selectedTier.cost_breakdown || {}) : costBreakdown;
              const hasItems = Object.keys(cb).length > 0 && !editing;
              if (!hasItems) return null;
              return (
                <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-white mb-4">Cost Breakdown</h3>
                  <div className="space-y-2 text-sm">
                    {Object.entries(cb).map(([key, value]) => value && parseFloat(value) > 0 && (
                      <div key={key} className="flex justify-between"><span className="text-zinc-400">{key}</span><span className="text-white">{formatCurrency(value)}</span></div>
                    ))}
                  </div>
                </div>
              );
            })()}

            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Timeline</h3>
              <div className="space-y-2 text-sm">
                <div><span className="text-zinc-500">Created:</span> <span className="text-white ml-2">{formatDate(deal.created_at)}</span></div>
                <div><span className="text-zinc-500">Updated:</span> <span className="text-white ml-2">{formatDate(deal.updated_at)}</span></div>
                {deal.closed_at && <div><span className="text-zinc-500">Closed:</span> <span className="text-white ml-2">{formatDate(deal.closed_at)}</span></div>}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'contacts' && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
          <div className="flex justify-between items-center mb-4"><h3 className="text-lg font-semibold text-white">Contacts</h3><button onClick={() => setShowAddContact(true)} className="bg-nextide-turquoise text-black px-4 py-2 rounded-lg text-sm font-medium">+ Add</button></div>
          {contacts.length === 0 ? <div className="text-zinc-500 text-center py-8">No contacts</div> : (
            <div className="space-y-2">{contacts.map(c => (
              <div key={c.id} className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg">
                <div><div className="text-white font-medium">{c.name || c.email} {c.is_primary && <span className="text-xs bg-nextide-turquoise/20 text-nextide-turquoise px-2 py-0.5 rounded ml-2">Primary</span>}</div><div className="text-zinc-500 text-sm">{c.email} {c.company && `• ${c.company}`}</div></div>
                <button onClick={() => removeContact(c.id)} className="text-zinc-500 hover:text-red-400">Remove</button>
              </div>
            ))}</div>
          )}
        </div>
      )}

      {activeTab === 'talent' && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-white">Linked Talent List</h3>
            <div className="flex gap-2 items-center">
              {talentList ? (
                <>
                  <a href={`/talent-lists/${talentList.id}`} className="text-nextide-turquoise text-sm hover:underline">Open Full List</a>
                  <button onClick={() => linkTalentList(null)} disabled={linkingList}
                    className="bg-zinc-800 text-red-400 px-3 py-1.5 rounded-lg text-sm hover:bg-red-500/20">Unlink</button>
                </>
              ) : (
                <select onChange={e => { if (e.target.value) linkTalentList(parseInt(e.target.value)); }}
                  disabled={linkingList} value=""
                  className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-1.5 text-white text-sm">
                  <option value="">Link a talent list...</option>
                  {allTalentLists.filter(l => !l.deal_id || l.deal_id === parseInt(id)).map(l => (
                    <option key={l.id} value={l.id}>{l.name} ({l.item_count || 0} creators)</option>
                  ))}
                </select>
              )}
            </div>
          </div>

          {talentList ? (
            <div>
              <div className="flex items-center gap-4 mb-4 text-sm">
                <span className="text-white font-semibold">{talentList.name}</span>
                <span className="text-zinc-400">{talentItems.length} creators</span>
              </div>
              {talentItems.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[700px]">
                    <thead><tr className="border-b border-zinc-700">
                      <th className="text-left p-2 text-zinc-400 text-xs">Creator</th>
                      <th className="text-left p-2 text-zinc-400 text-xs">Tier</th>
                      <th className="text-left p-2 text-zinc-400 text-xs">Category</th>
                      <th className="text-right p-2 text-zinc-400 text-xs">Following</th>
                      <th className="text-right p-2 text-zinc-400 text-xs">CCV</th>
                      <th className="text-right p-2 text-zinc-400 text-xs">BSS</th>
                      <th className="text-right p-2 text-zinc-400 text-xs">M%</th>
                      <th className="text-right p-2 text-zinc-400 text-xs">18-34%</th>
                      <th className="text-left p-2 text-zinc-400 text-xs">Agency</th>
                    </tr></thead>
                    <tbody>
                      {talentItems.map(t => (
                        <tr key={t.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                          <td className="p-2"><a href={`/talent/${t.talent_id}`} className="text-white text-sm font-medium hover:text-nextide-turquoise">{t.name}</a></td>
                          <td className="p-2"><span className={`px-1.5 py-0.5 rounded text-xs font-semibold ${t.talent_tier===1?'bg-amber-500/20 text-amber-400':t.talent_tier===2?'bg-zinc-400/20 text-zinc-300':t.talent_tier===3?'bg-orange-800/20 text-orange-400':'bg-zinc-800 text-zinc-600'}`}>T{t.talent_tier||'-'}</span></td>
                          <td className="p-2 text-zinc-400 text-xs">{t.primary_category || '-'}</td>
                          <td className="p-2 text-nextide-turquoise text-xs text-right">{t.total_following ? (t.total_following >= 1e6 ? (t.total_following/1e6).toFixed(1)+'M' : t.total_following >= 1e3 ? Math.round(t.total_following/1e3)+'K' : t.total_following) : '-'}</td>
                          <td className="p-2 text-purple-400 text-xs text-right">{t.twitch_avg_ccv > 0 ? (t.twitch_avg_ccv >= 1e3 ? (t.twitch_avg_ccv/1e3).toFixed(1)+'K' : t.twitch_avg_ccv) : '-'}</td>
                          <td className="p-2 text-xs text-right">{t.brand_safety_score ? parseFloat(t.brand_safety_score).toFixed(1) : '-'}</td>
                          <td className="p-2 text-zinc-300 text-xs text-right">{t.aud_male ? (parseFloat(t.aud_male)*100).toFixed(1)+'%' : '-'}</td>
                          <td className="p-2 text-zinc-300 text-xs text-right">{t.aud_18_34 ? (parseFloat(t.aud_18_34)*100).toFixed(1)+'%' : '-'}</td>
                          <td className="p-2 text-zinc-500 text-xs">{t.agency || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : <div className="text-zinc-500 text-sm">List has no creators yet</div>}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="text-zinc-500 text-sm mb-2">No talent list linked to this deal</div>
              <div className="text-zinc-600 text-xs">Select a list above, or create one in the Talent tab first</div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'io' && (() => {
        // Compute total spend from all tiers for payment term calculations
        const allPlacements = tiers.flatMap(t => t.placements || []);
        const totalSpend = allPlacements.filter(p => p && p.rate_type !== 'av').reduce((s, p) => s + (parseFloat(p.total) || 0), 0);
        const selectedBE = billingEntities.find(be => String(be.id) === String(ioForm.billing_entity_id));

        return (
          <div className="space-y-6">
            {/* Header bar */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-white">Insertion Order</h2>
                <p className="text-zinc-500 text-sm mt-0.5">Configure and export your IO for {deal.name}</p>
              </div>
              <button onClick={exportIO}
                className="bg-nextide-turquoise text-black px-5 py-2.5 rounded-lg font-semibold text-sm hover:bg-nextide-turquoise/90">
                Export PDF
              </button>
            </div>

            {/* IO Details */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 space-y-4">
              <h3 className="text-sm font-semibold text-zinc-400 uppercase tracking-wide">IO Details</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-zinc-500 mb-1">IO Number</label>
                  <input type="text" value={ioForm.io_number}
                    onChange={e => setIoForm(f => ({ ...f, io_number: e.target.value }))}
                    placeholder="e.g. IO-2026-001"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-nextide-turquoise"/>
                </div>
                <div>
                  <label className="block text-xs text-zinc-500 mb-1">Campaign</label>
                  <div className="w-full bg-zinc-800/50 border border-zinc-700/50 rounded-lg px-3 py-2 text-zinc-400 text-sm">{deal.name}</div>
                </div>
                <div>
                  <label className="block text-xs text-zinc-500 mb-1">Advertiser</label>
                  <div className="w-full bg-zinc-800/50 border border-zinc-700/50 rounded-lg px-3 py-2 text-zinc-400 text-sm">{deal.account || '—'}</div>
                </div>
                <div>
                  <label className="block text-xs text-zinc-500 mb-1">Flight Dates</label>
                  <div className="w-full bg-zinc-800/50 border border-zinc-700/50 rounded-lg px-3 py-2 text-zinc-400 text-sm">
                    {deal.campaign_start_date ? new Date(deal.campaign_start_date).toLocaleDateString() : '—'}
                    {' → '}
                    {deal.campaign_end_date ? new Date(deal.campaign_end_date).toLocaleDateString() : '—'}
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-xs text-zinc-500 mb-1">Internal Notes (not on PDF)</label>
                <textarea value={ioForm.notes} onChange={e => setIoForm(f => ({ ...f, notes: e.target.value }))} rows={2}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm resize-none focus:outline-none focus:border-nextide-turquoise"
                  placeholder="Any notes for internal reference..."/>
              </div>
            </div>

            {/* Bill To */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-zinc-400 uppercase tracking-wide">Bill To</h3>
                <button onClick={() => { setEditingBillingId(null); setBillToForm({ company_name: '', attention: '', address_line1: '', address_line2: '', city: '', state: '', zip: '', country: 'USA', email: '', is_default: false }); setShowBillToModal(true); }}
                  className="text-xs text-nextide-turquoise hover:text-nextide-turquoise/80 bg-nextide-turquoise/10 px-3 py-1.5 rounded-lg border border-nextide-turquoise/20">
                  + New Entity
                </button>
              </div>

              {billingEntities.length === 0 ? (
                <div className="text-zinc-500 text-sm text-center py-4">No billing entities yet — add one above.</div>
              ) : (
                <div className="space-y-2">
                  {billingEntities.map(be => (
                    <div key={be.id}
                      onClick={() => setIoForm(f => ({ ...f, billing_entity_id: String(be.id) }))}
                      className={`flex items-start justify-between p-4 rounded-xl border cursor-pointer transition-all ${String(ioForm.billing_entity_id) === String(be.id) ? 'border-nextide-turquoise bg-nextide-turquoise/5' : 'border-zinc-800 bg-zinc-800/30 hover:border-zinc-600'}`}>
                      <div className="flex items-start gap-3">
                        <div className={`w-4 h-4 rounded-full border-2 mt-0.5 flex-shrink-0 ${String(ioForm.billing_entity_id) === String(be.id) ? 'border-nextide-turquoise bg-nextide-turquoise' : 'border-zinc-600'}`}/>
                        <div>
                          <div className="text-white text-sm font-medium flex items-center gap-2">
                            {be.company_name}
                            {be.is_default && <span className="text-[10px] bg-nextide-turquoise/20 text-nextide-turquoise px-1.5 py-0.5 rounded">Default</span>}
                          </div>
                          {be.attention && <div className="text-zinc-500 text-xs">Attn: {be.attention}</div>}
                          <div className="text-zinc-500 text-xs">
                            {[be.address_line1, be.city, be.state, be.zip].filter(Boolean).join(', ')}
                          </div>
                          {be.email && <div className="text-zinc-500 text-xs">{be.email}</div>}
                        </div>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <button onClick={e => { e.stopPropagation(); setEditingBillingId(be.id); setBillToForm({ company_name: be.company_name, attention: be.attention || '', address_line1: be.address_line1 || '', address_line2: be.address_line2 || '', city: be.city || '', state: be.state || '', zip: be.zip || '', country: be.country || 'USA', email: be.email || '', is_default: be.is_default }); setShowBillToModal(true); }}
                          className="text-zinc-500 hover:text-white text-xs px-2 py-1 bg-zinc-700 rounded">Edit</button>
                        <button onClick={e => { e.stopPropagation(); deleteBillingEntity(be.id); }}
                          className="text-zinc-500 hover:text-red-400 text-xs px-2 py-1 bg-zinc-700 rounded">×</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Payment Terms */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 space-y-4">
              <h3 className="text-sm font-semibold text-zinc-400 uppercase tracking-wide">Payment Terms</h3>
              <div className="space-y-3">
                {ioPaymentTerms.map((term, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 bg-zinc-800/50 rounded-lg border border-zinc-800">
                    <div className="flex-1">
                      <input type="text" value={term.label}
                        onChange={e => setIoPaymentTerms(prev => prev.map((t, i) => i === idx ? { ...t, label: e.target.value } : t))}
                        className="w-full bg-transparent text-white text-sm focus:outline-none"/>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <input type="number" value={term.pct}
                        onChange={e => {
                          const pct = parseFloat(e.target.value) || 0;
                          setIoPaymentTerms(prev => prev.map((t, i) => i === idx ? { ...t, pct, amount: totalSpend * pct / 100 } : t));
                        }}
                        className="w-14 bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-white text-sm text-right focus:outline-none"/>
                      <span className="text-zinc-500 text-xs">%</span>
                    </div>
                    <div className="text-nextide-turquoise font-semibold text-sm w-24 text-right shrink-0">
                      {formatCurrency(totalSpend * (term.pct || 0) / 100)}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <span className="text-zinc-500 text-xs">Net</span>
                      <input type="number" value={term.net}
                        onChange={e => setIoPaymentTerms(prev => prev.map((t, i) => i === idx ? { ...t, net: parseInt(e.target.value) || 0 } : t))}
                        className="w-12 bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-white text-sm text-right focus:outline-none"/>
                      <span className="text-zinc-500 text-xs">days</span>
                    </div>
                  </div>
                ))}
                <button onClick={() => setIoPaymentTerms(prev => [...prev, { label: 'Additional Payment', pct: 0, net: 30 }])}
                  className="text-xs text-zinc-500 hover:text-white">+ Add term</button>
              </div>
            </div>

            {/* Placement summary */}
            {tiers.length > 0 && (
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
                <h3 className="text-sm font-semibold text-zinc-400 uppercase tracking-wide mb-4">Placements Summary</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-zinc-700">
                        <th className="text-left pb-2 text-zinc-500 text-xs font-medium">Placement</th>
                        <th className="text-right pb-2 text-zinc-500 text-xs font-medium">Impressions</th>
                        <th className="text-right pb-2 text-zinc-500 text-xs font-medium">CPM</th>
                        <th className="text-right pb-2 text-zinc-500 text-xs font-medium">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tiers.map(tier => (
                        (tier.placements || []).filter(p => p.rate_type !== 'av' && p.rate_type !== 'desc_only').map(p => (
                          <tr key={p.id} className="border-b border-zinc-800/50">
                            <td className="py-2 text-white">{tiers.length > 1 && <span className="text-zinc-600 text-xs mr-2">T{tier.tier_number}</span>}{p.name}</td>
                            <td className="py-2 text-right text-zinc-400 text-xs">{p.impressions ? parseInt(p.impressions).toLocaleString() : '—'}</td>
                            <td className="py-2 text-right text-zinc-400 text-xs">{p.rate_type === 'flat' ? 'Flat' : p.cpm ? `$${p.cpm}` : '—'}</td>
                            <td className="py-2 text-right text-nextide-turquoise font-medium">{p.total ? formatCurrency(p.total) : '—'}</td>
                          </tr>
                        ))
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-zinc-700">
                        <td className="pt-2 text-white font-semibold text-xs" colSpan={3}>Total Spend</td>
                        <td className="pt-2 text-right text-nextide-turquoise font-bold">{formatCurrency(totalSpend)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}

            {/* Save button */}
            <div className="flex justify-end">
              <button onClick={saveIO} disabled={ioSaving}
                className="bg-zinc-700 text-white px-6 py-2.5 rounded-lg text-sm font-semibold hover:bg-zinc-600 disabled:opacity-50">
                {ioSaving ? 'Saving...' : 'Save IO'}
              </button>
            </div>

            {/* Bill To Modal */}
            {showBillToModal && (
              <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" onClick={() => setShowBillToModal(false)}>
                <div className="bg-zinc-900 border border-zinc-800 rounded-xl w-full max-w-md p-6 space-y-4" onClick={e => e.stopPropagation()}>
                  <h3 className="text-lg font-bold text-white">{editingBillingId ? 'Edit' : 'New'} Billing Entity</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs text-zinc-500 mb-1">Company Name *</label>
                      <input type="text" value={billToForm.company_name} onChange={e => setBillToForm(f => ({ ...f, company_name: e.target.value }))}
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-nextide-turquoise"/>
                    </div>
                    <div>
                      <label className="block text-xs text-zinc-500 mb-1">Attention / Contact Name</label>
                      <input type="text" value={billToForm.attention} onChange={e => setBillToForm(f => ({ ...f, attention: e.target.value }))}
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-nextide-turquoise"/>
                    </div>
                    <div>
                      <label className="block text-xs text-zinc-500 mb-1">Address Line 1</label>
                      <input type="text" value={billToForm.address_line1} onChange={e => setBillToForm(f => ({ ...f, address_line1: e.target.value }))}
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-nextide-turquoise"/>
                    </div>
                    <div>
                      <label className="block text-xs text-zinc-500 mb-1">Address Line 2</label>
                      <input type="text" value={billToForm.address_line2} onChange={e => setBillToForm(f => ({ ...f, address_line2: e.target.value }))}
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-nextide-turquoise"/>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <label className="block text-xs text-zinc-500 mb-1">City</label>
                        <input type="text" value={billToForm.city} onChange={e => setBillToForm(f => ({ ...f, city: e.target.value }))}
                          className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none"/>
                      </div>
                      <div>
                        <label className="block text-xs text-zinc-500 mb-1">State</label>
                        <input type="text" value={billToForm.state} onChange={e => setBillToForm(f => ({ ...f, state: e.target.value }))}
                          className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none"/>
                      </div>
                      <div>
                        <label className="block text-xs text-zinc-500 mb-1">ZIP</label>
                        <input type="text" value={billToForm.zip} onChange={e => setBillToForm(f => ({ ...f, zip: e.target.value }))}
                          className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none"/>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs text-zinc-500 mb-1">Billing Email</label>
                      <input type="email" value={billToForm.email} onChange={e => setBillToForm(f => ({ ...f, email: e.target.value }))}
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-nextide-turquoise"/>
                    </div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={billToForm.is_default} onChange={e => setBillToForm(f => ({ ...f, is_default: e.target.checked }))}/>
                      <span className="text-zinc-400 text-sm">Set as default billing entity</span>
                    </label>
                  </div>
                  <div className="flex gap-3 pt-2">
                    <button onClick={() => setShowBillToModal(false)} className="flex-1 bg-zinc-800 text-white py-2.5 rounded-lg text-sm">Cancel</button>
                    <button onClick={saveBillingEntity} disabled={!billToForm.company_name}
                      className="flex-1 bg-nextide-turquoise text-black py-2.5 rounded-lg text-sm font-semibold disabled:opacity-50">Save</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {activeTab === 'activity' && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
          <div className="flex justify-between items-center mb-4"><h3 className="text-lg font-semibold text-white">Activity</h3><button onClick={() => setShowAddActivity(true)} className="bg-nextide-turquoise text-black px-4 py-2 rounded-lg text-sm font-medium">+ Log</button></div>
          {activities.length === 0 ? <div className="text-zinc-500 text-center py-8">No activities</div> : (
            <div className="space-y-3">{activities.map(a => (
              <div key={a.id} className="p-3 bg-zinc-800 rounded-lg">
                <div className="flex justify-between items-start">
                  <div><div className="text-white font-medium">{a.subject || a.activity_type}</div>{a.description && <div className="text-zinc-400 text-sm mt-1">{a.description}</div>}<div className="text-zinc-500 text-xs mt-2">{a.user_name} • {formatDateTime(a.activity_date)}</div></div>
                  <span className="text-xs bg-zinc-700 text-zinc-400 px-2 py-1 rounded capitalize">{a.activity_type}</span>
                </div>
              </div>
            ))}</div>
          )}
        </div>
      )}

      {activeTab === 'reports' && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
          <CampaignReport dealId={id} deal={deal} />
        </div>
      )}

      {activeTab === 'files' && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-white">Files</h3>
            <div className="flex gap-2">
              <label className="bg-zinc-800 text-white px-3 py-2 rounded-lg text-sm cursor-pointer hover:bg-zinc-700">📊 Media Plan<input type="file" className="hidden" onChange={e => uploadFile(e, 'media_plan')}/></label>
              <label className="bg-zinc-800 text-white px-3 py-2 rounded-lg text-sm cursor-pointer hover:bg-zinc-700">👥 Talent List<input type="file" className="hidden" onChange={e => uploadFile(e, 'talent_list')}/></label>
              <label className="bg-zinc-800 text-white px-3 py-2 rounded-lg text-sm cursor-pointer hover:bg-zinc-700">📎 Other<input type="file" className="hidden" onChange={e => uploadFile(e, 'other')}/></label>
            </div>
          </div>
          {uploading && <div className="text-zinc-400 text-center py-4">Uploading...</div>}
          {files.length === 0 ? <div className="text-zinc-500 text-center py-8">No files</div> : (
            <div className="space-y-2">{files.map(f => (
              <div key={f.id} className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg">
                <div><div className="text-white">{f.file_name}</div><div className="text-zinc-500 text-xs">{f.file_type} • {(f.file_size/1024).toFixed(1)}KB • {formatDate(f.created_at)}</div></div>
                <div className="flex gap-2"><button onClick={() => downloadFile(f.id, f.file_name)} className="text-nextide-turquoise">Download</button><button onClick={() => deleteFile(f.id)} className="text-zinc-500 hover:text-red-400">Delete</button></div>
              </div>
            ))}</div>
          )}
        </div>
      )}

      {activeTab === 'media plan' && (() => {
        const pkgsByTier = (pkg) => placements.filter(p => p.package === pkg);
        const fmtNum = (n) => n >= 1e6 ? (n/1e6).toFixed(1)+'M' : n >= 1e3 ? Math.round(n/1e3)+'K' : String(n);
        const rowProps = { updatePlacement, deletePlacement, duplicatePlacement, autoCalc, tiers, activeTierId, formatTierLabel };


        const PackageSection = ({ title, pkg, color = 'text-zinc-400', addOptions }) => {
          const rows = pkgsByTier(pkg);
          return (
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden mb-4">
              <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800 bg-zinc-900/80">
                <span className={`text-sm font-semibold ${color}`}>{title}</span>
                <div className="flex gap-2">
                  {addOptions.map(opt => (
                    <button key={opt.name} onClick={() => addPlacement(pkg, opt.name, opt.rateType || 'cpm')}
                      className="text-xs text-zinc-400 hover:text-white bg-zinc-800 hover:bg-zinc-700 px-2 py-1 rounded transition-colors">
                      + {opt.label || opt.name}
                    </button>
                  ))}
                </div>
              </div>
              {rows.length === 0 ? (
                <div className="px-4 py-5 text-zinc-600 text-xs text-center">No placements — add one above</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[800px]">
                    <thead>
                      <tr className="text-xs text-zinc-600 border-b border-zinc-800/50">
                        <th className="px-3 py-2 text-left">Placement</th>
                        <th className="px-2 py-2 text-left">Description</th>
                        <th className="px-2 py-2 text-right">Impressions</th>
                        <th className="px-2 py-2 text-right">CPM</th>
                        <th className="px-2 py-2 text-right">Total</th>
                        <th className="px-2 py-2 text-left">Date Override</th>
                        <th className="w-16"/>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map(p => <PlacementRow key={p.id} p={p} {...rowProps} />)}
                    </tbody>
                  </table>
                </div>
              )}
              {/* Package subtotal */}
              {rows.length > 0 && (() => {
                const subImpr = rows.reduce((s, p) => s + (parseInt(p.impressions) || 0), 0);
                const subRev = rows.filter(p => p.rate_type !== 'av').reduce((s, p) => s + (parseFloat(p.total) || 0), 0);
                return (
                  <div className="flex justify-end gap-6 px-4 py-2 bg-zinc-800/30 border-t border-zinc-800 text-xs text-zinc-500">
                    {subImpr > 0 && <span>{fmtNum(subImpr)} impr.</span>}
                    {subRev > 0 && <span className="text-zinc-300">{formatCurrency(subRev)}</span>}
                    {subRev === 0 && pkg === 'av' && <span className="text-zinc-600">Added Value</span>}
                  </div>
                );
              })()}
            </div>
          );
        };

        return (
          <div>
            {/* Tier tabs when multi-tier */}
            {deal.tiers_enabled && tiers.length > 0 && (
              <div className="flex items-center gap-2 mb-5">
                {tiers.map((tier, idx) => (
                  <button key={tier.id} onClick={() => { setSelectedTierIdx(idx); fetchPlacements(tier.id); }}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${selectedTierIdx === idx ? 'bg-nextide-turquoise text-black' : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'}`}>
                    {formatTierLabel(tier)}
                  </button>
                ))}
              </div>
            )}

            {placementsLoading ? (
              <div className="text-zinc-500 text-center py-12">Loading placements...</div>
            ) : (
              <>
                {/* Creator Media */}
                <PackageSection
                  title="Creator Media"
                  pkg="creator_media"
                  color="text-nextide-turquoise"
                  addOptions={[
                    { name: 'OmniBanners', label: 'OmniBanners', rateType: 'cpm' },
                    { name: 'Immersive Frames', label: 'Immersive Frames', rateType: 'cpm' },
                    { name: 'Chatbot', label: 'Chatbot', rateType: 'desc_only' },
                    { name: 'Panel', label: 'Panel', rateType: 'desc_only' },
                    { name: 'Custom Line', label: 'Other', rateType: 'cpm' },
                  ]}
                />

                {/* Custom Activation */}
                <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden mb-4">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800 bg-zinc-900/80">
                    <div className="flex items-center gap-2">
                      {editingCustomName ? (
                        <input autoFocus type="text" value={customPackageName}
                          onChange={e => setCustomPackageName(e.target.value)}
                          onBlur={() => setEditingCustomName(false)}
                          onKeyDown={e => e.key === 'Enter' && setEditingCustomName(false)}
                          className="bg-zinc-800 border border-zinc-700 rounded px-2 py-0.5 text-white text-sm font-semibold focus:outline-none focus:border-nextide-turquoise w-48"/>
                      ) : (
                        <button onClick={() => setEditingCustomName(true)}
                          className="text-sm font-semibold text-yellow-400 hover:text-yellow-300 flex items-center gap-1">
                          {customPackageName} <span className="text-zinc-600 text-xs">✎</span>
                        </button>
                      )}
                    </div>
                    <div className="flex gap-2 items-center">
                      <button onClick={() => addPlacement('custom', 'Sponsored Stream', 'flat')}
                        className="text-xs text-zinc-400 hover:text-white bg-zinc-800 hover:bg-zinc-700 px-2 py-1 rounded">
                        + Sponsored Stream
                      </button>
                      <button onClick={() => addPlacement('custom', 'Social Post', 'flat')}
                        className="text-xs text-zinc-400 hover:text-white bg-zinc-800 hover:bg-zinc-700 px-2 py-1 rounded">
                        + Social Post
                      </button>
                      <CustomAddInput onAdd={(name) => addPlacement('custom', name, 'flat')} />
                    </div>
                  </div>
                  {pkgsByTier('custom').length === 0 ? (
                    <div className="px-4 py-5 text-zinc-600 text-xs text-center">No placements — add one above</div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[800px]">
                        <thead>
                          <tr className="text-xs text-zinc-600 border-b border-zinc-800/50">
                            <th className="px-3 py-2 text-left">Placement</th>
                            <th className="px-2 py-2 text-left">Description</th>
                            <th className="px-2 py-2 text-right">Impressions</th>
                            <th className="px-2 py-2 text-right">CPM</th>
                            <th className="px-2 py-2 text-right">Total</th>
                            <th className="px-2 py-2 text-left">Date Override</th>
                            <th className="w-16"/>
                          </tr>
                        </thead>
                        <tbody>
                          {pkgsByTier('custom').map(p => <PlacementRow key={p.id} p={p} {...rowProps} />)}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {pkgsByTier('custom').length > 0 && (() => {
                    const rows = pkgsByTier('custom');
                    const subImpr = rows.reduce((s, p) => s + (parseInt(p.impressions) || 0), 0);
                    const subRev = rows.reduce((s, p) => s + (parseFloat(p.total) || 0), 0);
                    return (
                      <div className="flex justify-end gap-6 px-4 py-2 bg-zinc-800/30 border-t border-zinc-800 text-xs text-zinc-500">
                        {subImpr > 0 && <span>{fmtNum(subImpr)} impr.</span>}
                        {subRev > 0 && <span className="text-zinc-300">{formatCurrency(subRev)}</span>}
                      </div>
                    );
                  })()}
                </div>

                {/* Added Value */}
                <PackageSection
                  title="Added Value"
                  pkg="av"
                  color="text-zinc-400"
                  addOptions={[
                    { name: 'Brand Lift Study', label: 'Brand Lift Study', rateType: 'av' },
                    { name: 'Creative Production', label: 'Production', rateType: 'av' },
                    { name: 'Usage Rights', label: 'Usage Rights', rateType: 'av' },
                    { name: 'Added Value', label: 'Other AV', rateType: 'av' },
                  ]}
                />

                {/* Tier Totals Bar */}
                {placements.length > 0 && (
                  <div className="bg-zinc-900 border border-zinc-700 rounded-xl px-6 py-4 flex items-center gap-8">
                    <div className="text-xs text-zinc-500 uppercase tracking-wide font-semibold">Tier Totals</div>
                    {tierTotals.totalImpressions > 0 && (
                      <div>
                        <div className="text-xs text-zinc-500">Total Impressions</div>
                        <div className="text-white font-semibold">{fmtNum(tierTotals.totalImpressions)}</div>
                      </div>
                    )}
                    {tierTotals.totalRevenue > 0 && (
                      <div>
                        <div className="text-xs text-zinc-500">Total Spend</div>
                        <div className="text-nextide-turquoise font-bold text-lg">{formatCurrency(tierTotals.totalRevenue)}</div>
                      </div>
                    )}
                    {tierTotals.blendedCpm && (
                      <div>
                        <div className="text-xs text-zinc-500">Blended eCPM</div>
                        <div className="text-white font-semibold">${tierTotals.blendedCpm}</div>
                      </div>
                    )}
                    <div className="ml-auto">
                      <button onClick={async () => {
                        try {
                          const res = await api.get(`/deals/${id}/media-plan/export`, { responseType: 'blob' });
                          const url = window.URL.createObjectURL(new Blob([res.data]));
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `${(deal.name || 'Media_Plan').replace(/[^a-zA-Z0-9 ]/g,'')}_Media_Plan.xlsx`;
                          document.body.appendChild(a); a.click(); a.remove();
                        } catch (e) { alert('Export failed'); }
                      }} className="bg-nextide-turquoise text-black px-4 py-2 rounded-lg text-sm font-semibold hover:bg-nextide-turquoise/90">
                        Export .xlsx
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        );
      })()}

      {showAddContact && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-8">
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl w-full max-w-md p-6">
            <h3 className="text-xl font-bold text-white mb-4">Add Contact</h3>
            <select value={selectedContact} onChange={e => setSelectedContact(e.target.value)} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white mb-4"><option value="">Select...</option>{unlinkedContacts.map(c => <option key={c.id} value={c.id}>{c.name || c.email} - {c.company}</option>)}</select>
            <div className="flex gap-3"><button onClick={() => setShowAddContact(false)} className="flex-1 bg-zinc-800 text-white px-4 py-2 rounded-lg">Cancel</button><button onClick={addContact} disabled={!selectedContact} className="flex-1 bg-nextide-turquoise text-black px-4 py-2 rounded-lg disabled:opacity-50">Add</button></div>
          </div>
        </div>
      )}

      {showAddActivity && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-8">
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl w-full max-w-md p-6">
            <h3 className="text-xl font-bold text-white mb-4">Log Activity</h3>
            <form onSubmit={addActivity} className="space-y-4">
              <div><label className="block text-sm text-zinc-400 mb-1">Type</label><select value={newActivity.activity_type} onChange={e => setNewActivity({...newActivity, activity_type: e.target.value})} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"><option value="note">Note</option><option value="call">Call</option><option value="meeting">Meeting</option><option value="email">Email</option><option value="task">Task</option></select></div>
              <div><label className="block text-sm text-zinc-400 mb-1">Subject</label><input type="text" value={newActivity.subject} onChange={e => setNewActivity({...newActivity, subject: e.target.value})} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white" required/></div>
              <div><label className="block text-sm text-zinc-400 mb-1">Description</label><textarea value={newActivity.description} onChange={e => setNewActivity({...newActivity, description: e.target.value})} rows={3} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"/></div>
              <div className="flex gap-3"><button type="button" onClick={() => setShowAddActivity(false)} className="flex-1 bg-zinc-800 text-white px-4 py-2 rounded-lg">Cancel</button><button type="submit" className="flex-1 bg-nextide-turquoise text-black px-4 py-2 rounded-lg">Save</button></div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DealDetail;
